import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Tag, CircleNotch } from "@phosphor-icons/react";
import AdminNav from "../../components/AdminNav";

const MODES = [
  { value: "GLOBAL_ONCE", label: "One use globally" },
  { value: "PER_SENDER", label: "One use per sender" },
  { value: "PER_SCO", label: "One use per sender company" },
  { value: "UNLIMITED", label: "Unlimited while active" },
];
const modeLabel = (v) => MODES.find((m) => m.value === v)?.label || v;
const inr = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

export default function PromoCodes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState("");
  const [mode, setMode] = useState("GLOBAL_ONCE");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo-codes");
      setRows(data);
    } catch {
      toast.error("Could not load promo codes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const c = code.trim().toUpperCase();
    if (!/^[A-Z0-9-]+$/.test(c)) return toast.error("Letters, numbers and hyphens only");
    const d = Number(discount);
    if (!Number.isInteger(d) || d <= 0) return toast.error("Discount must be a positive whole rupee amount");
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", { code: c, flat_discount_inr: d, usage_mode: mode });
      toast.success(`Promo ${c} created`);
      setCode(""); setDiscount(""); setMode("GLOBAL_ONCE");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not create promo code");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (r) => {
    try {
      await api.post(`/admin/promo-codes/${r.id}/${r.is_active ? "deactivate" : "reactivate"}`);
      await load();
    } catch {
      toast.error("Could not update promo code");
    }
  };

  return (
    <>
    <div className="max-w-[1600px] mx-auto px-6 pt-6"><AdminNav /></div>
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="promo-codes-page">
      <div className="flex items-center gap-2 mb-6">
        <Tag size={22} weight="bold" />
        <h1 className="heading text-2xl">Promo Codes</h1>
      </div>

      {/* Create */}
      <div className="rounded-[3px] border border-[hsl(var(--border))] p-5 mb-8" data-testid="promo-create-card">
        <div className="overline mb-3">Create a promo code</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <label className="block sm:col-span-2">
            <span className="text-xs text-[hsl(var(--muted))]">Promo Code</span>
            <input
              className="input-field mt-1"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Letters, numbers and hyphens only"
              data-testid="promo-code-input"
            />
          </label>
          <label className="block">
            <span className="text-xs text-[hsl(var(--muted))]">Flat Discount ₹</span>
            <input
              type="number" min="1" step="1"
              className="input-field mt-1"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="e.g. 500"
              data-testid="promo-discount-input"
            />
          </label>
          <label className="block">
            <span className="text-xs text-[hsl(var(--muted))]">Usage Mode</span>
            <select className="input-field mt-1" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="promo-mode-select">
              {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </label>
        </div>
        <button className="btn-primary mt-4" onClick={create} disabled={busy} data-testid="promo-create-button">
          {busy ? (<><CircleNotch size={14} className="animate-spin" /> Creating…</>) : "Create promo code"}
        </button>
      </div>

      {/* List */}
      <div className="overline mb-2">Existing promo codes</div>
      {loading ? (
        <div className="text-sm text-[hsl(var(--muted))]" data-testid="promo-list-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-[hsl(var(--muted))]" data-testid="promo-list-empty">No promo codes yet.</div>
      ) : (
        <div className="divide-y divide-[hsl(var(--border))] border border-[hsl(var(--border))] rounded-[3px]" data-testid="promo-list">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`promo-row-${r.code}`}>
              <div className="font-mono font-semibold" data-testid={`promo-row-code-${r.code}`}>{r.code}</div>
              <div className="text-sm">₹{inr(r.flat_discount_inr)} off</div>
              <div className="text-xs text-[hsl(var(--muted))]" data-testid={`promo-row-mode-${r.code}`}>{modeLabel(r.usage_mode)}</div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${r.is_active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}
                data-testid={`promo-row-status-${r.code}`}
              >
                {r.is_active ? "Active" : "Inactive"}
              </span>
              <div className="text-xs text-[hsl(var(--muted))]" data-testid={`promo-row-redeemed-${r.code}`}>
                {r.usage_mode === "GLOBAL_ONCE"
                  ? (r.claimed_count > 0 ? "Used (exhausted)" : "Available")
                  : `${r.redeemed_count} redeemed`}
              </div>
              <button
                className="btn-ghost ml-auto text-sm"
                onClick={() => toggle(r)}
                data-testid={`promo-toggle-${r.code}`}
              >
                {r.is_active ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
